import { Check } from 'lucide-react';

import { cn } from '../lib/cn.js';

export interface StepperStep {
  id: string;
  label: string;
  /** Optional one-line description shown under the label. */
  description?: string;
}

interface StepperProps {
  steps: StepperStep[];
  currentIndex: number;
  className?: string;
}

/**
 * Horizontal step indicator used by the CSV-import wizard. Pure
 * presentational — no internal state, the caller drives `currentIndex`.
 */
export function Stepper({ steps, currentIndex, className }: StepperProps) {
  return (
    <ol
      className={cn('flex flex-wrap items-start gap-2 text-sm', className)}
      aria-label="Progress"
    >
      {steps.map((step, index) => {
        const isCompleted = index < currentIndex;
        const isCurrent = index === currentIndex;
        return (
          <li key={step.id} className="flex flex-1 items-start gap-2 min-w-[12rem]">
            <div
              className={cn(
                'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold tabular-nums',
                isCompleted
                  ? 'border-primary bg-primary text-primary-fg'
                  : isCurrent
                    ? 'border-primary text-primary'
                    : 'border-border text-fg-muted',
              )}
              aria-current={isCurrent ? 'step' : undefined}
            >
              {isCompleted ? <Check className="h-3.5 w-3.5" aria-hidden /> : index + 1}
            </div>
            <div className="min-w-0">
              <div
                className={cn(
                  'truncate font-medium',
                  isCurrent ? 'text-fg' : isCompleted ? 'text-fg-secondary' : 'text-fg-muted',
                )}
              >
                {step.label}
              </div>
              {step.description ? (
                <p className="truncate text-xs text-fg-muted">{step.description}</p>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
